import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminManageInventory.css';
import { useProductManagement } from '../../hooks/useProductManagement';
import { sendLowStockAlertEmail } from '../../firebase/email';
import {
    SIZE_KEY_TO_DISPLAY,
    SIZE_DISPLAY_TO_KEY,
    ADULT_SIZES_DISPLAY as ADULT_SIZES,
    KIDS_SIZES_DISPLAY  as KIDS_SIZES,
} from '../../utils/sizeUtils';

const LOW_STOCK_THRESHOLD = 10;

const COLOR_MAP = {
    White:'#f5f5f5', Black:'#1f1f1f', Red:'#e53935', Blue:'#1a5faa',
    Green:'#2e7d32', Navy:'#1a237e', Gray:'#888', Yellow:'#f9a825',
    Orange:'#e65100', Pink:'#e91e8c', Purple:'#7b1fa2', Brown:'#5d4037',
};

const COLOR_PRESETS = ['White', 'Black', 'Red', 'Blue', 'Green', 'Navy',
    'Gray', 'Yellow', 'Orange', 'Pink', 'Purple', 'Brown'];

function hasColorVariants(product) {
    return Array.isArray(product.colors) && product.colors.length > 0;
}

function hasSizeVariants(product) {
    return !!product.sizeTier;
}

function hasDynamicAttributes(product) {
    const attrs = product.selectedAttributes || product.attributes;
    if (!attrs || typeof attrs !== 'object' || Array.isArray(attrs)) return false;
    return Object.keys(attrs).length > 0;
}

function hasSizeAttribute(product) {
    const attrs = product.selectedAttributes || product.attributes || {};
    if (!attrs || typeof attrs !== 'object' || Array.isArray(attrs)) return false;
    return Object.keys(attrs).some(key => key.toLowerCase() === 'size');
}

function requiresSizeTier(product) {
    if (!product || typeof product !== 'object') return false;
    if (hasSizeAttribute(product)) return true;
    if (product.sizePrices && typeof product.sizePrices === 'object' && Object.keys(product.sizePrices).length > 0) return true;
    if (Array.isArray(product.availableSizes) && product.availableSizes.length > 0) return true;
    return false;
}

function hasLegacyInventory(product) {
    return hasColorVariants(product) || (hasSizeVariants(product) && !hasDynamicAttributes(product));
}

function needsDynamicAttributeStockModal(product) {
    return hasDynamicAttributes(product);
}

function buildInventoryFromProduct(product) {
    const sizeTier = product.sizeTier || null;
    const colors   = Array.isArray(product.colors) ? product.colors : [];
    const sizes    = sizeTier === 'Kids' ? KIDS_SIZES : ADULT_SIZES;
    let stock = {};
    let attributeKeys = [];

    const attrs = product.selectedAttributes || product.attributes;
    if (attrs && typeof attrs === 'object' && !Array.isArray(attrs)) {
        attributeKeys = Object.keys(attrs);
    }

    if (colors.length > 0 && hasDynamicAttributes(product)) {
        attributeKeys = ['Color', ...attributeKeys];
    }

    const inventoryObject = product.inventory && typeof product.inventory === 'object' && !Array.isArray(product.inventory)
        ? product.inventory : undefined;

    if (inventoryObject && Object.keys(inventoryObject).length > 0) {
        stock = inventoryObject;
    } else if (colors.length > 0 && !hasDynamicAttributes(product)) {
        colors.forEach(color => {
            stock[color] = {};
            const sizesToUse = sizeTier ? sizes : ['Qty'];
            sizesToUse.forEach(size => { stock[color][size] = 0; });
        });
    } else if (hasDynamicAttributes(product)) {
        const existing = inventoryObject || {};
        const attrValues = [];
        if (colors.length > 0) {
            attrValues.push(colors);
        }
        const dynamicValues = (product.selectedAttributes || product.attributes) || {};
        Object.keys(dynamicValues).forEach(key => {
            const attrData = dynamicValues[key];
            if (Array.isArray(attrData)) {
                attrValues.push(attrData.map(item => typeof item === 'object' ? item.value : item));
            } else if (typeof attrData === 'object' && attrData !== null && 'value' in attrData) {
                attrValues.push([attrData.value]);
            } else {
                attrValues.push([]);
            }
        });
        
        if (attrValues.length > 0 && attrValues.every(arr => arr.length > 0)) {
            const combinations = attrValues.reduce((acc, vals) => {
                return acc.flatMap(combo => vals.map(v => [...combo, v]));
            }, [[]]);
            combinations.forEach(combo => {
                const key = attributeKeys.reduce((str, k, i) => str + (i > 0 ? ':' : '') + combo[i], '');
                stock[key] = existing[key] ?? 0;
            });
        }
    } else if (!sizeTier) {
        stock = { Qty: product.stockQuantity ?? 0 };
    } else {
        colors.forEach(color => {
            stock[color] = {};
            sizes.forEach(size => { stock[color][size] = 0; });
        });
    }

    return { ...product, colors, sizeTier, stock, subcategory: product.subcategory || '', attributeKeys };
}

function getProductSummary(product) {
    let totalStock = 0;
    const hasEmptyStock = product.stock && Object.keys(product.stock).length === 0;
    if (hasEmptyStock && typeof product.stockQuantity === 'number') {
        totalStock = product.stockQuantity;
    } else {
        Object.values(product.stock).forEach((cs) => {
            if (typeof cs === 'number') {
                totalStock += cs || 0;
            } else if (cs && typeof cs === 'object') {
                totalStock += Object.values(cs).reduce((s, v) => s + (v || 0), 0);
            }
        });
        if (totalStock === 0 && typeof product.stockQuantity === 'number') {
            totalStock = product.stockQuantity;
        }
    }
    const isLow = totalStock > 0 && totalStock <= LOW_STOCK_THRESHOLD;
    return { totalStock, hasLow: isLow };
}

// ─── Tab bar ──────────────────────────────────────────────────────────────────
function ProductTabBar({ active }) {
    const navigate = useNavigate();
    const tabs = [
        { label: 'Manage Products',   path: '/admin/products/manage' },
        { label: 'Manage Category', path: '/admin/products/categories' },
        { label: 'Manage Subcategory', path: '/admin/products/subcategories' },
        { label: 'Manage Submenus', path: '/admin/products/submenus' },
        { label: 'Manage Attributes', path: '/admin/products/attributes' },
        { label: 'Manage Inventory',  path: '/admin/products/inventory' },
    ];
    return (
        <div className="mi-tab-bar">
            {tabs.map(t => (
                <button key={t.path} className={`mi-tab${active === t.label ? ' mi-tab--active' : ''}`} onClick={() => navigate(t.path)}>
                    {t.label}
                </button>
            ))}
        </div>
    );
}

// ─── StockCell ────────────────────────────────────────────────────────────────
function StockCell({ value, onChange, disabled }) {
    const [display, setDisplay] = useState(value === 0 ? '' : String(value));

    useEffect(() => { setDisplay(value === 0 ? '' : String(value)); }, [value]);

    const isZero = value === 0;
    const isLow  = value > 0 && value <= LOW_STOCK_THRESHOLD;

    return (
        <input
            type="number"
            min="0"
            value={display}
            placeholder="0"
            disabled={disabled}
            onBlur={() => {
                const parsed = Math.max(0, parseInt(display) || 0);
                setDisplay(parsed === 0 ? '' : String(parsed));
                onChange(parsed);
            }}
            onChange={e => setDisplay(e.target.value)}
            className={`mi-stock-input${
                disabled
                    ? ' mi-stock-input--disabled'
                    : isZero
                        ? ' mi-stock-input--zero'
                        : isLow
                            ? ' mi-stock-input--low'
                            : ''
            }`}
        />
    );
}

// ─── No-Color Stock Modal (Polo Shirt / Jersey) ───────────────────────────────
function NoColorStockModal({ product, onClose, onSave }) {
    const sizeTier = product.sizeTier || 'Adults';
    const allSizes = sizeTier === 'Kids' ? KIDS_SIZES : ADULT_SIZES;

    const getAvailableSizeSet = () => {
        if (Array.isArray(product.availableSizes) && product.availableSizes.length > 0) {
            return new Set(product.availableSizes.map(k => SIZE_KEY_TO_DISPLAY[k] || k));
        }
        if (product.sizePrices && typeof product.sizePrices === 'object') {
            const available = new Set();
            Object.entries(product.sizePrices).forEach(([key, val]) => {
                if (val !== null && val !== undefined) {
                    const display = SIZE_KEY_TO_DISPLAY[key];
                    if (display) available.add(display);
                }
            });
            if (available.size > 0) return available;
        }
        return new Set(allSizes);
    };

    const availableSizeSet = getAvailableSizeSet();

    const [draft, setDraft] = useState(() => {
        const existing = product.stock?.['_nocolor'] || {};
        const row = {};
        allSizes.forEach(size => { row[size] = existing[size] ?? 0; });
        return row;
    });

    const updateCell = (size, val) => setDraft(prev => ({ ...prev, [size]: val }));

    const totalStock = allSizes
        .filter(s => availableSizeSet.has(s))
        .reduce((s, size) => s + (draft[size] || 0), 0);

    return (
        <div className="mi-modal-overlay" onClick={onClose}>
            <div className="mi-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
                <div className="mi-modal-header">
                    <div className="mi-modal-header-info">
                        <h2 className="mi-modal-title">{product.name}</h2>
                        <span className="mi-modal-subtitle">
                            {product.category} · {product.subcategory} · {sizeTier} Sizes · No color variants
                        </span>
                    </div>
                    <button className="mi-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="mi-modal-body">
                    <div className="mi-legend">
                        <span className="mi-legend-item mi-legend-item--zero">0 = Out of stock</span>
                        <span className="mi-legend-item mi-legend-item--low">≤{LOW_STOCK_THRESHOLD} = Low stock</span>
                        <span className="mi-legend-item mi-legend-item--ok">Normal</span>
                        <span className="mi-legend-item" style={{ background:'#f5f5f5', color:'#aaa' }}>— = Size not available for this product</span>
                    </div>

                    <div className="mi-banner mi-banner--info" style={{ margin: 0 }}>
                        This product has no color options. Stock is tracked by size only.
                    </div>

                    <div className="mi-matrix-wrap">
                        <table className="mi-matrix">
                            <thead>
                                <tr>
                                    {allSizes.map(size => (
                                        <th
                                            key={size}
                                            style={!availableSizeSet.has(size)
                                                ? { color: '#ccc', textDecoration: 'line-through' }
                                                : {}
                                            }
                                            title={!availableSizeSet.has(size) ? `${size} is not available for this product` : size}
                                        >
                                            {size}
                                        </th>
                                    ))}
                                    <th className="mi-matrix-total-col">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    {allSizes.map(size => (
                                        <td key={size} className="mi-matrix-data-cell">
                                            <StockCell
                                                value={draft[size] || 0}
                                                onChange={val => updateCell(size, val)}
                                                disabled={!availableSizeSet.has(size)}
                                            />
                                        </td>
                                    ))}
                                    <td className="mi-matrix-row-total">{totalStock}</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    {allSizes.map(size => (
                                        !availableSizeSet.has(size)
                                            ? <td key={size} className="mi-matrix-col-total" style={{ color: '#ccc' }}>—</td>
                                            : <td key={size} className="mi-matrix-col-total">{draft[size] || 0}</td>
                                    ))}
                                    <td className="mi-matrix-grand-total">{totalStock}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <div className="mi-modal-footer">
                    <span className="mi-total-summary">Total stock: <strong>{totalStock}</strong></span>
                    <div className="mi-modal-footer-actions">
                        <button className="mi-btn-secondary" onClick={onClose}>Cancel</button>
                        <button
                            className="mi-btn-primary"
                            onClick={() => onSave(draft, totalStock)}
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Color Stock Modal (T-Shirt, etc.) ───────────────────────────────────────
function StockModal({ product, onClose, onSave }) {
    const hasSizes = !!product.sizeTier;
    const sizes = hasSizes ? (product.sizeTier === 'Kids' ? KIDS_SIZES : ADULT_SIZES) : ['Qty'];

    const [draft, setDraft] = useState(() => {
        const copy = {};
        product.colors.forEach(color => {
            copy[color] = {};
            sizes.forEach(size => { copy[color][size] = product.stock[color]?.[size] ?? 0; });
        });
        return copy;
    });

    const [newColorInput, setNewColorInput] = useState('');

    const isSizeAvailable = (size) => {
        if (!hasSizes) return true;
        if (!product.sizePrices) return true;
        const key = SIZE_DISPLAY_TO_KEY[size] || size;
        return (
            product.sizePrices[key] !== null && product.sizePrices[key] !== undefined
        ) || (
            product.sizePrices[size] !== null && product.sizePrices[size] !== undefined
        );
    };

    const updateCell = (color, size, val) =>
        setDraft(prev => ({ ...prev, [color]: { ...prev[color], [size]: val } }));

    const addColor = (colorName) => {
        const val = colorName || newColorInput.trim();
        if (!val || draft[val]) return;
        const newRow = {};
        sizes.forEach(size => { newRow[size] = 0; });
        setDraft(prev => ({ ...prev, [val]: newRow }));
        setNewColorInput('');
    };

    const removeColor = (color) => {
        setDraft(prev => {
            const copy = { ...prev };
            delete copy[color];
            return copy;
        });
    };

    const totalStock = Object.values(draft).reduce(
        (t, cs) => t + Object.values(cs).reduce((s, v) => s + (v || 0), 0), 0
    );

    const colorList = Object.keys(draft);

    return (
        <div className="mi-modal-overlay" onClick={onClose}>
            <div className="mi-modal" onClick={e => e.stopPropagation()}>
                <div className="mi-modal-header">
                    <div className="mi-modal-header-info">
                        <h2 className="mi-modal-title">{product.name}</h2>
                        <span className="mi-modal-subtitle">
                            {product.category} · {product.subcategory} · {product.sizeTier || 'No size tier set'} Sizes
                        </span>
                    </div>
                    <button className="mi-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="mi-modal-body">
                    <div className="mi-legend">
                        <span className="mi-legend-item mi-legend-item--zero">0 = Out of stock</span>
                        <span className="mi-legend-item mi-legend-item--low">≤{LOW_STOCK_THRESHOLD} = Low stock</span>
                        <span className="mi-legend-item mi-legend-item--ok">Normal</span>
                        <span className="mi-legend-item" style={{ background:'#f5f5f5', color:'#aaa' }}>— = Size not available for this product</span>
                    </div>

                    <div className="mi-add-color-section">
                        <span className="mi-add-color-label">Add color variant:</span>
                        <div className="mi-add-color-presets">
                            {COLOR_PRESETS.filter(c => !draft[c]).map(c => (
                                <button
                                    key={c}
                                    type="button"
                                    className="mi-color-preset-btn"
                                    style={{
                                        background: COLOR_MAP[c] || '#ccc',
                                        border: c === 'White' ? '1.5px solid #ddd' : 'none'
                                    }}
                                    title={`Add ${c}`}
                                    onClick={() => addColor(c)}
                                />
                            ))}
                        </div>
                        <div className="mi-add-color-custom">
                            <input
                                type="text"
                                placeholder="Custom color name…"
                                value={newColorInput}
                                onChange={e => setNewColorInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && addColor()}
                            />
                            <button type="button" className="mi-add-color-btn" onClick={() => addColor()}>
                                Add
                            </button>
                        </div>
                    </div>

                    {colorList.length === 0 ? (
                        <div className="mi-matrix-empty">No color variants defined. Add a color above to start tracking stock.</div>
                    ) : (
                        <div className="mi-matrix-wrap">
                            <table className="mi-matrix">
                                <thead>
                                    <tr>
                                        <th className="mi-matrix-corner">Color \ Size</th>
                                        {sizes.map(size => (
                                            <th
                                                key={size}
                                                style={!isSizeAvailable(size)
                                                    ? { color: '#ccc', textDecoration: 'line-through' }
                                                    : {}
                                                }
                                                title={!isSizeAvailable(size) ? `${size} is not available for this product` : size}
                                            >
                                                {size}
                                            </th>
                                        ))}
                                        <th className="mi-matrix-total-col">Total</th>
                                        <th className="mi-matrix-remove-col"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {colorList.map(color => {
                                        const colorTotal = sizes
                                            .filter(size => isSizeAvailable(size))
                                            .reduce((s, size) => s + (draft[color]?.[size] || 0), 0);
                                        return (
                                            <tr key={color}>
                                                <td className="mi-matrix-color-cell">
                                                    <span
                                                        className="mi-color-dot"
                                                        style={{ background: COLOR_MAP[color] || '#ccc', border: color === 'White' ? '1px solid #ddd' : 'none' }}
                                                    />
                                                    {color}
                                                </td>
                                                {sizes.map(size => (
                                                    <td key={size} className="mi-matrix-data-cell">
                                                        <StockCell
                                                            value={draft[color]?.[size] || 0}
                                                            onChange={val => updateCell(color, size, val)}
                                                            disabled={!isSizeAvailable(size)}
                                                        />
                                                    </td>
                                                ))}
                                                <td className="mi-matrix-row-total">{colorTotal}</td>
                                                <td className="mi-matrix-remove-col">
                                                    <button
                                                        type="button"
                                                        className="mi-remove-color-btn"
                                                        onClick={() => removeColor(color)}
                                                        title={`Remove ${color}`}
                                                    >
                                                        ×
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td className="mi-matrix-footer-label">Column Total</td>
                                        {sizes.map(size => {
                                            if (!isSizeAvailable(size)) {
                                                return <td key={size} className="mi-matrix-col-total" style={{ color: '#ccc' }}>—</td>;
                                            }
                                            const colTotal = colorList.reduce((s, c) => s + (draft[c]?.[size] || 0), 0);
                                            return <td key={size} className="mi-matrix-col-total">{colTotal}</td>;
                                        })}
                                        <td className="mi-matrix-grand-total">{totalStock}</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>

                <div className="mi-modal-footer">
                    <span className="mi-total-summary">Total stock: <strong>{totalStock}</strong></span>
                    <div className="mi-modal-footer-actions">
                        <button className="mi-btn-secondary" onClick={onClose}>Cancel</button>
                        <button
                            className="mi-btn-primary"
                            onClick={() => onSave(draft, colorList)}
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Simple Stock Modal (Non-Clothing products) ───────────────────────────────
function SimpleStockModal({ product, onClose, onSave }) {
    const [draft, setDraft] = useState({
        stockQuantity: typeof product.stockQuantity === 'number' ? product.stockQuantity : 0,
    });

    const handleChange = (value) => {
        const num = Math.max(0, parseInt(value) || 0);
        setDraft({ stockQuantity: num });
    };

    return (
        <div className="mi-modal-overlay" onClick={onClose}>
            <div className="mi-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
                <div className="mi-modal-header">
                    <div className="mi-modal-header-info">
                        <h2 className="mi-modal-title">{product.name}</h2>
                        <span className="mi-modal-subtitle">
                            {product.category} · {product.subcategory || '—'}
                        </span>
                    </div>
                    <button className="mi-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="mi-modal-body">
                    <div className="mi-banner mi-banner--info" style={{ margin: 0 }}>
                        This product uses simple stock tracking (no sizes or colors).
                    </div>

                    <div className="mi-field" style={{ marginTop: 16 }}>
                        <label>Stock Quantity</label>
                        <input
                            type="number"
                            min="0"
                            value={draft.stockQuantity || ''}
                            onChange={e => handleChange(e.target.value)}
                            placeholder="0"
                        />
                    </div>
                </div>

                <div className="mi-modal-footer">
                    <span className="mi-total-summary">Total stock: <strong>{draft.stockQuantity}</strong></span>
                    <div className="mi-modal-footer-actions">
                        <button className="mi-btn-secondary" onClick={onClose}>Cancel</button>
                        <button className="mi-btn-primary" onClick={() => onSave(draft.stockQuantity)}>
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
function DynamicAttributeStockModal({ product, onClose, onSave }) {
    const attrs = product.selectedAttributes || product.attributes || {};
    const baseAttributeKeys = Object.keys(attrs);
    const hasColors = Array.isArray(product.colors) && product.colors.length > 0;
    const attributeKeys = hasColors ? ['Color', ...baseAttributeKeys] : baseAttributeKeys;
    
    const getAttrValues = (key) => {
        if (hasColors && key === 'Color') return product.colors;
        const attrData = attrs[key];
        if (Array.isArray(attrData)) {
            return attrData.map(item => typeof item === 'object' ? item.value : item);
        }
        if (typeof attrData === 'object' && attrData !== null && 'value' in attrData) {
            return [attrData.value];
        }
        return [];
    };

    const [draft, setDraft] = useState(() => {
        const existing = product.inventory || product.stock || {};
        const attrValues = attributeKeys.map(getAttrValues);
        if (attrValues.length === 0 || attrValues.some(arr => arr.length === 0)) {
            return {};
        }
        const combinations = attrValues.reduce((acc, vals) => {
            return acc.flatMap(combo => vals.map(v => [...combo, v]));
        }, [[]]);
        const result = {};
        combinations.forEach(combo => {
            const key = attributeKeys.reduce((str, k, i) => str + (i > 0 ? ':' : '') + combo[i], '');
            result[key] = existing[key] ?? 0;
        });
        return result;
    });

    const updateStock = (key, val) => {
        setDraft(prev => ({ ...prev, [key]: Math.max(0, parseInt(val) || 0) }));
    };

    const totalStock = Object.values(draft).reduce((sum, v) => sum + (v || 0), 0);
    const keys = Object.keys(draft);
    
    const getAttrDisplay = (key) => {
        const parts = key.split(':');
        return parts.map((v, i) => `${attributeKeys[i]}:${v}`).join('|');
    };

    return (
        <div className="mi-modal-overlay" onClick={onClose}>
            <div className="mi-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
                <div className="mi-modal-header">
                    <div className="mi-modal-header-info">
                        <h2 className="mi-modal-title">{product.name}</h2>
                        <span className="mi-modal-subtitle">
                            {product.category} · {product.subcategory || '—'} · Dynamic Attributes
                        </span>
                    </div>
                    <button className="mi-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="mi-modal-body">
                    <div className="mi-banner mi-banner--info" style={{ margin: 0 }}>
                        This product uses custom attributes for inventory tracking.
                    </div>

                    <div className="mi-matrix-wrap">
                        <table className="mi-matrix">
                            <thead>
                                <tr>
                                    {attributeKeys.map(k => <th key={k}>{k}</th>)}
                                    <th>Stock</th>
                                </tr>
                            </thead>
                            <tbody>
                                {keys.length === 0 ? (
                                    <tr><td colSpan={attributeKeys.length + 1} className="mi-empty">No attribute combinations defined.</td></tr>
                                ) : keys.map(key => {
                                    const parts = key.split(':');
                                    return (
                                        <tr key={key}>
                                            {parts.map((part, i) => (
                                                <td key={i} className="mi-matrix-data-cell">{part}</td>
                                            ))}
                                            <td className="mi-matrix-data-cell">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={draft[key] || ''}
                                                    placeholder="0"
                                                    onChange={e => updateStock(key, e.target.value)}
                                                    className="mi-stock-input"
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td className="mi-matrix-grand-total" colSpan={attributeKeys.length}>Total</td>
                                    <td className="mi-matrix-grand-total">{totalStock}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <div className="mi-modal-footer">
                    <span className="mi-total-summary">Total stock: <strong>{totalStock}</strong></span>
                    <div className="mi-modal-footer-actions">
                        <button className="mi-btn-secondary" onClick={onClose}>Cancel</button>
                        <button className="mi-btn-primary" onClick={() => onSave(draft, totalStock)}>
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
function CategoryInventoryTable({ categoryName, products, pageSize, setEditTarget }) {
    const [page, setPage] = useState(1);
    
    useEffect(() => { setPage(1); }, [pageSize, products.length]);
    
    const totalPages = Math.max(1, Math.ceil(products.length / pageSize));
    const paginated = products.slice((page - 1) * pageSize, page * pageSize);
    const hasAnySizeTier = products.some(p => p.sizeTier);

    return (
        <div className="mi-category-block" style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#111', borderBottom: '2px solid #eaeaea', paddingBottom: '8px' }}>
                {categoryName}
            </h3>
            <div className="mi-table-wrap">
                <table className="mi-table">
                    <thead>
                        <tr>
                            <th>Product Name</th>
                            <th>Subcategory</th>
                            {hasAnySizeTier && <th>Size Tier</th>}
                            <th>Colors</th>
                            <th>Total Stock</th>
                            <th>Stock Status</th>
                            <th>Last Updated</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginated.length === 0 ? (
                            <tr><td colSpan={hasAnySizeTier ? 8 : 7} className="mi-empty">No products found.</td></tr>
                        ) : paginated.map(product => {
                            const { totalStock, hasLow } = getProductSummary(product);
                            const isOut = totalStock === 0;
                            const hasDynamic = hasDynamicAttributes(product);
                            const hasColors = Array.isArray(product.colors) && product.colors.length > 0;
                            return (
                                <tr key={product.id} className={isOut ? 'mi-row--out' : hasLow ? 'mi-row--low' : ''}>
                                    <td className="mi-product-name">{product.name}</td>
                                    <td>{product.subcategory || <span className="mi-no-tier">—</span>}</td>
                                    {hasAnySizeTier && <td>{product.sizeTier || <span className="mi-no-tier">Not set</span>}</td>}
                                    <td>
                                        {hasDynamic ? (
                                            <span style={{ fontSize: 12, color: '#888', fontStyle: 'italic' }}>Dynamic Attributes</span>
                                        ) : hasColors ? (
                                            <div className="mi-color-dots">
                                                {product.colors.map(c => (
                                                    <span key={c} className="mi-color-dot" style={{ background: COLOR_MAP[c] || '#ccc', border: c === 'White' ? '1px solid #ddd' : 'none' }} title={c} />
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="mi-no-colors">—</span>
                                        )}
                                    </td>
                                    <td className="mi-total-stock">{totalStock}</td>
                                    <td>
                                        {isOut
                                            ? <span className="mi-stock-badge mi-stock-badge--out">Out of Stock</span>
                                            : hasLow
                                                ? <span className="mi-stock-badge mi-stock-badge--low">Low Stock</span>
                                                : <span className="mi-stock-badge mi-stock-badge--ok">In Stock</span>
                                        }
                                    </td>
                                    <td className="mi-updated">
                                        {product.updatedAt
                                            ? (product.updatedAt.seconds
                                                ? new Date(product.updatedAt.seconds * 1000).toLocaleDateString('en-PH', { year:'numeric', month:'short', day:'numeric' })
                                                : String(product.updatedAt))
                                            : '—'}
                                    </td>
                                    <td>
                                        <button
                                            className="mi-edit-stock-btn"
                                            onClick={() => setEditTarget(product)}
                                            title="Edit Stock"
                                        >
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                            Edit Stock
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            
            <div className="mi-pagination">
                <span className="mi-result-count">
                    Showing {products.length === 0 ? 0 : Math.min((page - 1) * pageSize + 1, products.length)}–{Math.min(page * pageSize, products.length)} of {products.length}
                </span>
                <button className="mi-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
                <span className="mi-page-divider">|</span>
                <button className="mi-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
        </div>
    );
}

export default function AdminManageInventory() {
    const { products, loading, error, fetchProducts, updateProductEntry } = useProductManagement([]);

    const [search,       setSearch]      = useState('');
    const [pageSize,     setPageSize]    = useState(10);
    const [editTarget,   setEditTarget]  = useState(null);
    const [actionError,  setActionError] = useState(null);

    const [filterCategory,    setFilterCategory]    = useState('all');
    const [filterSubcategory, setFilterSubcategory] = useState('all');
    const [filterStatus,      setFilterStatus]      = useState('all');
    const [sortBy,            setSortBy]            = useState('name-asc');

    useEffect(() => { fetchProducts(); }, [fetchProducts]);

    const inventory = useMemo(() => products.map(buildInventoryFromProduct), [products]);

    const categories = useMemo(() => {
        const cats = [...new Set(inventory.map(p => p.category).filter(Boolean))].sort();
        return cats;
    }, [inventory]);
    
    const subcategories = useMemo(() => {
        const subs = [...new Set(inventory.map(p => p.subcategory).filter(Boolean))].sort();
        return subs;
    }, [inventory]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        let list = inventory.filter(p => {
            const matchSearch = !q ||
                p.name.toLowerCase().includes(q) ||
                p.category.toLowerCase().includes(q) ||
                (p.subcategory || '').toLowerCase().includes(q);
            const matchCat = filterCategory === 'all' || p.category === filterCategory;
            const matchSub = filterSubcategory === 'all' || p.subcategory === filterSubcategory;
            if (!matchSearch || !matchCat || !matchSub) return false;
            if (filterStatus === 'all') return true;
            const { totalStock, hasLow } = getProductSummary(p);
            if (filterStatus === 'out')    return totalStock === 0;
            if (filterStatus === 'low')    return totalStock > 0 && hasLow;
            if (filterStatus === 'in')     return totalStock > 0 && !hasLow;
            return true;
        });

        list = [...list].sort((a, b) => {
            if (sortBy === 'name-asc')    return a.name.localeCompare(b.name);
            if (sortBy === 'name-desc')   return b.name.localeCompare(a.name);
            if (sortBy === 'stock-asc')   return getProductSummary(a).totalStock - getProductSummary(b).totalStock;
            if (sortBy === 'stock-desc')  return getProductSummary(b).totalStock - getProductSummary(a).totalStock;
            return 0;
        });

        return list;
    }, [inventory, search, filterCategory, filterSubcategory, filterStatus, sortBy]);

    const productsByCategory = useMemo(() => {
        return filtered.reduce((acc, p) => {
            const cat = p.category || 'Uncategorized';
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(p);
            return acc;
        }, {});
    }, [filtered]);

    const handleSaveNoColorStock = async (draft, totalStock) => {
        setActionError(null);
        if (!editTarget.sizeTier && !hasSizeAttribute(editTarget)) {
            setActionError('Set a size tier on this product in Manage Products before editing inventory.');
            return;
        }
        if (!editTarget.id) {
            setActionError('Product ID is missing — cannot save inventory.');
            return;
        }
        const result = await updateProductEntry(editTarget.id, {
            inventory:     { '_nocolor': draft },
            sizeTier:      editTarget.sizeTier,
            stockQuantity: totalStock,
        });
        if (result.error) { setActionError(result.error); return; }

        const lowStockSizes = Object.entries(draft)
            .filter(([, qty]) => qty <= LOW_STOCK_THRESHOLD)
            .map(([size, qty]) => ({ size, stock: qty }));

        if (lowStockSizes.length > 0) {
            sendLowStockAlertEmail({
                productName:  editTarget.name,
                subcategory:  editTarget.subcategory || '',
                lowStockSizes,
                trigger:      'manual',
            }).catch(err => console.error('handleSaveNoColorStock: low stock alert failed:', err));
        }

        await fetchProducts();
        setEditTarget(null);
    };

    const handleSaveStock = async (draft, colorList) => {
        setActionError(null);
        if (requiresSizeTier(editTarget) && !editTarget.sizeTier) {
            setActionError('Set a size tier on this product in Manage Products before editing inventory.');
            return;
        }
        const stockQuantity = Object.values(draft).reduce(
            (t, cs) => t + Object.values(cs).reduce((s, v) => s + (v || 0), 0), 0
        );
        const result = await updateProductEntry(editTarget.id, {
            inventory:     draft,
            colors:        colorList,
            sizeTier:      editTarget.sizeTier,
            stockQuantity,
        });
        if (result.error) { setActionError(result.error); return; }

        const lowStockSizes = [];
        colorList.forEach(color => {
            Object.entries(draft[color] || {}).forEach(([size, qty]) => {
                if (qty <= LOW_STOCK_THRESHOLD) {
                    lowStockSizes.push({ size: `${color} / ${size}`, stock: qty });
                }
            });
        });

        if (lowStockSizes.length > 0) {
            sendLowStockAlertEmail({
                productName:  editTarget.name,
                subcategory:  editTarget.subcategory || '',
                lowStockSizes,
                trigger:      'manual',
            }).catch(err => console.error('handleSaveStock: low stock alert failed:', err));
        }

        setEditTarget(null);
    };

    const handleSaveSimpleStock = async (stockQuantity) => {
        setActionError(null);
        if (!editTarget.id) {
            setActionError('Product ID is missing — cannot save inventory.');
            return;
        }
        const result = await updateProductEntry(editTarget.id, { stockQuantity });
        if (result.error) { setActionError(result.error); return; }

        if (stockQuantity > 0 && stockQuantity <= LOW_STOCK_THRESHOLD) {
            sendLowStockAlertEmail({
                productName:  editTarget.name,
                subcategory:  editTarget.subcategory || '',
                lowStockSizes: [{ size: 'Simple stock', stock: stockQuantity }],
                trigger:      'manual',
            }).catch(err => console.error('handleSaveSimpleStock: low stock alert failed:', err));
        }

        await fetchProducts();
        setEditTarget(null);
    };

    const handleSaveDynamicAttributeStock = async (draft, totalStock) => {
        setActionError(null);
        if (!editTarget.id) {
            setActionError('Product ID is missing — cannot save inventory.');
            return;
        }
        const result = await updateProductEntry(editTarget.id, { inventory: draft, stockQuantity: totalStock });
        if (result.error) { setActionError(result.error); return; }

        const lowStockItems = Object.entries(draft)
            .filter(([, qty]) => qty <= LOW_STOCK_THRESHOLD)
            .map(([key, qty]) => ({ size: key, stock: qty }));

        if (lowStockItems.length > 0) {
            sendLowStockAlertEmail({
                productName:  editTarget.name,
                subcategory:  editTarget.subcategory || '',
                lowStockSizes: lowStockItems,
                trigger:      'manual',
            }).catch(err => console.error('handleSaveDynamicAttributeStock: low stock alert failed:', err));
        }

        await fetchProducts();
        setEditTarget(null);
    };

    return (
        <div className="mi-page">
            <ProductTabBar active="Manage Inventory" />

            <div className="mi-info-banner">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                Inventory tracks stock by size and color per product. Click <strong>Edit Stock</strong> to update quantities.
                Low stock threshold: <strong>{LOW_STOCK_THRESHOLD} units</strong>
            </div>

            {loading && <div className="mi-banner mi-banner--info">Loading inventory…</div>}
            {(error || actionError) && <div className="mi-banner mi-banner--error">{error || actionError}</div>}

            {/* Toolbar */}
            <div className="mi-toolbar">
                <div className="mi-toolbar-left">
                    <div className="mi-entries-label">
                        Show
                        <select className="mi-entries-select" value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                            {[10, 15, 20, 25].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                        entries
                    </div>
                </div>
                <div className="mi-toolbar-right">
                    <div className="mi-search-wrap">
                        <input type="text" className="mi-search" placeholder="Search products…" value={search} onChange={e => setSearch(e.target.value)} />
                        <svg className="mi-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                    </div>
                </div>
            </div>

            {/* Filter + Sort bar */}
            <div className="mi-filter-bar">
                <div className="mi-filter-group">
                    <label className="mi-filter-label">Category</label>
                    <select className="mi-filter-select" value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
                        <option value="all">All Categories</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                </div>
                
                <div className="mi-filter-group">
                    <label className="mi-filter-label">Subcategory</label>
                    <select className="mi-filter-select" value={filterSubcategory} onChange={e => setFilterSubcategory(e.target.value)}>
                        <option value="all">All Subcategories</option>
                        {subcategories.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                </div>
                    
                <div className="mi-filter-group">
                    <label className="mi-filter-label">Stock Status</label>
                    <select className="mi-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                        <option value="all">All Statuses</option>
                        <option value="in">In Stock</option>
                        <option value="low">Low Stock</option>
                        <option value="out">Out of Stock</option>
                        </select>
                </div>
                
                <div className="mi-filter-group">
                    <label className="mi-filter-label">Sort By</label>
                    <select className="mi-filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                        <option value="name-asc">Name A–Z</option>
                        <option value="name-desc">Name Z–A</option>
                        <option value="stock-asc">Stock ↑ (Low first)</option>
                        <option value="stock-desc">Stock ↓ (High first)</option>
                        </select>
                </div>
                {(filterCategory !== 'all' || filterSubcategory !== 'all' || filterStatus !== 'all' || sortBy !== 'name-asc') && (
                    <button
                    className="mi-filter-clear"
                    onClick={() => { setFilterCategory('all'); setFilterSubcategory('all'); setFilterStatus('all'); setSortBy('name-asc'); }}
                    >
                        Clear filters
                        </button>
                    )}
        </div>

            {/* Tables per category */}
            {Object.keys(productsByCategory).length === 0 ? (
                <div className="mi-table-wrap">
                    <table className="mi-table">
                        <tbody>
                            <tr><td className="mi-empty">No products found.</td></tr>
                        </tbody>
                    </table>
                </div>
            ) : (
                Object.entries(productsByCategory).map(([categoryName, products]) => (
                    <CategoryInventoryTable
                        key={categoryName}
                        categoryName={categoryName}
                        products={products}
                        pageSize={pageSize}
                        setEditTarget={setEditTarget}
                    />
                ))
            )}

            {/* Modals */}
            {editTarget && needsDynamicAttributeStockModal(editTarget) && (
                <DynamicAttributeStockModal
                    product={editTarget}
                    onClose={() => { setEditTarget(null); setActionError(null); }}
                    onSave={handleSaveDynamicAttributeStock}
                />
            )}
            {editTarget && !needsDynamicAttributeStockModal(editTarget) && hasLegacyInventory(editTarget) && (
                <>
                    {(editTarget.colors && editTarget.colors.length > 0) && (
                        <StockModal
                            product={editTarget}
                            onClose={() => { setEditTarget(null); setActionError(null); }}
                            onSave={handleSaveStock}
                        />
                    )}
                    {(!editTarget.colors || editTarget.colors.length === 0) && editTarget.sizeTier && (
                        <NoColorStockModal
                            product={editTarget}
                            onClose={() => { setEditTarget(null); setActionError(null); }}
                            onSave={handleSaveNoColorStock}
                        />
                    )}
                </>
            )}
            {editTarget && !needsDynamicAttributeStockModal(editTarget) && !hasLegacyInventory(editTarget) && (
                <SimpleStockModal
                    product={editTarget}
                    onClose={() => { setEditTarget(null); setActionError(null); }}
                    onSave={handleSaveSimpleStock}
                />
            )}
        </div>
    );
}